/**
 * Theme preferences on this device, per user (docs/design/theming.md, "Who chooses
 * what"). Stored in localStorage; every access is guarded because storage can be
 * missing or throw (private mode, quota, blocked site data), and the app must still
 * render with defaults. Syncing a user's theme across devices is later work.
 */
import { z } from 'zod';
import { featuresSchema } from '../themes/types.js';

export const APPEARANCES = ['system', 'light', 'dark'] as const;
export type Appearance = (typeof APPEARANCES)[number];

export const themePreferenceSchema = z.object({
  themeId: z.string().min(1).max(40),
  /** Only matters for themes with an alternate look. */
  appearance: z.enum(APPEARANCES).default('system'),
  /** Features the user turned on or off on top of the theme's defaults. */
  features: featuresSchema.partial().default({}),
});
export type ThemePreference = z.output<typeof themePreferenceSchema>;

/** The scope for preferences made before anyone signs in on this device. */
export const DEVICE_SCOPE = 'device';

export function preferenceKey(app: string, scope: string): string {
  return `qrkey.${app}.theme.v1.${scope}`;
}

export interface PreferenceStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function defaultStorage(): PreferenceStorage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function readPreference(
  app: string,
  scope: string,
  storage: PreferenceStorage | null = defaultStorage(),
): ThemePreference | null {
  try {
    const raw = storage?.getItem(preferenceKey(app, scope));
    if (!raw) return null;
    const parsed = themePreferenceSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export function writePreference(
  app: string,
  scope: string,
  pref: ThemePreference,
  storage: PreferenceStorage | null = defaultStorage(),
): boolean {
  try {
    storage?.setItem(preferenceKey(app, scope), JSON.stringify(pref));
    return storage !== null;
  } catch {
    return false;
  }
}
